import { fetchWrapper } from '@/api/fetchWrapper'
import RequestException from '@/exceptions/RequestException'
import UserActivityException from '@/exceptions/UserActivityException'
import { serverUrl } from '@/globals/env'
import { useAuthStore } from '@/stores/auth'
import { isNetworkError, logError, log } from '@/utils'

type Stat = {
  task_name: string
  total: number
  icon: string
  task_duration: number
}

type AddStatPayload = {
  task_name: string
  total: number
}

/**
 * Fetches stats for the last 24 hours for the authenticated user.
 */
export async function fetchStats(): Promise<Stat[] | null> {
  try {
    const authStore = useAuthStore()

    if (!authStore.user) throw new UserActivityException('User not logged in.')

    const response = await fetchWrapper('stats', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authStore.user?.token}`
      }
    })

    if (!response.ok) {
      //blacklisted or invalid token
      if (response.status == 401) await authStore.destroyUserData()
      else {
        const errorText = await response.text().catch(() => 'Unknown error')
        throw new UserActivityException(`${errorText} ${response.status}`)
      }
    }

    return await response.json()
  } catch (error) {
    log('Failed to load stats:', error)
    return null
  }
}

/**
 * Adds a new stat entry.
 * @param payload - The task name and total value.
 */
export async function addStat(payload: AddStatPayload): Promise<void> {
  const authStore = useAuthStore()
  const maxRetries = 3
  const initialDelay = 3000 // in ms

  const attempt = async (retries: number, delay: number): Promise<void> => {
    try {
      if (!authStore.user?.token) return

      const request = new Request(serverUrl + 'stats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authStore.user?.token}`
        },
        body: JSON.stringify(payload)
      })

      const response = await fetch(request)

      if (!response.ok) {
        if (response.status == 401) await authStore.destroyUserData()
        else {
          throw new RequestException(
            `Error adding stat. ${response.statusText} status: ${response.status}`,
            request,
            response
          )
        }
      }
    } catch (error) {
      if (isNetworkError(error) && retries > 0) {
        console.warn(`Network error while adding stat. Retrying in ${delay}ms...`)
        await new Promise((resolve) => setTimeout(resolve, delay))
        return attempt(retries - 1, delay * 2) // Exponential backoff
      }

      logError('Failed to add stat:', error)
    }
  }

  return attempt(maxRetries, initialDelay)
}

/**
 * Mock function to get the user's token.
 */
function getAuthToken(): string {
  return 'your-auth-token' // Replace with actual token retrieval logic.
}
